import { inject, injectable } from 'inversify';

import { SceneItemKind } from '@/engine/app/interfaces';
import { ImagesMode, SliceType } from '@/engine/app/modules/images/interfaces';

import { TYPES } from '@/engine/ioc';
import { TYPES as ImagesTYPES } from '@/engine/app/modules/images/iocTypes';

import type { ViewsService } from '@/engine/app/Views.service';
import type { IObjectViewModel } from '@/engine/app/interfaces';
import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ImagesService } from '@/engine/app/modules/images/Images.service';

import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import * as THREE from 'three';

@injectable()
export class PointsSwitcherController {
  private _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _imagesService: ImagesService;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(ImagesTYPES.ImagesService) imagesService: ImagesService
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._imagesService = imagesService;
  }

  public get selectedSplineViewModel(): IObjectViewModel | undefined {
    return this._viewsService.currentView
      ?.getObjects()
      .filter((object) => object.kind === SceneItemKind.Spline)
      .find((object) => object.isSelected);
  }

  public prevPoint(splineViewModel: IObjectViewModel): void {
    const selectedIndex: number = splineViewModel.selectedIndex === undefined ? 0 : splineViewModel.selectedIndex + 1;
    this._mprAccordingToSelectedPointIndex(selectedIndex, splineViewModel);
  }

  public nextPoint(splineViewModel: IObjectViewModel): void {
    const selectedIndex: number = splineViewModel.selectedIndex === undefined ? 0 : splineViewModel.selectedIndex - 1;
    this._mprAccordingToSelectedPointIndex(selectedIndex, splineViewModel);
  }

  private _mprAccordingToSelectedPointIndex(index: number, splineViewModel: IObjectViewModel): void {
    const areaModel = this._planningAlgorithm.getValue(splineViewModel.attachments[0], DT.AreaMeasurement);

    if (areaModel === undefined) {
      throw new Error('Spline area model is undefined');
    }

    const splineModel = this._planningAlgorithm.getValue(splineViewModel.descriptor, DT.Spline);

    if (splineModel === undefined) {
      throw new Error('Spline model is undefined');
    }

    const basePointsLength = splineModel.value.basePts.length;
    if (index >= basePointsLength) index = 0;
    if (index < 0) index = basePointsLength - 1;
    splineViewModel.setSelectedIndex?.(index);

    const model = splineModel.value;
    const pt = model.basePts[index];
    const origin = new THREE.Plane()
      .setFromNormalAndCoplanarPoint(areaModel.normal, pt)
      .projectPoint(model.center, new THREE.Vector3());
    this._imagesService.syncSliceWithPlane(
      SliceType.SAGITTAL,
      origin,
      areaModel.normal,
      areaModel.orientationY,
      undefined,
      true
    );
    this._imagesService.syncSliceWithPlane(
      SliceType.SAGITTAL,
      origin,
      model.normal,
      origin.clone().sub(pt).cross(model.normal).cross(model.normal),
      SliceType.SAGITTAL,
      true
    );

    if (this._imagesService.imagesMode === ImagesMode.POLYLINE) return;

    this._imagesService.requestSliceByPoint(SliceType.SAGITTAL, origin);
    this._imagesService.requestRoiPoint(pt, SliceType.SAGITTAL);
  }
}
